import { Fieldset } from '@headlessui/react'
import clsx from 'clsx'
import { useCallback, useMemo, useState } from 'react'
import { FieldValues, useForm } from 'react-hook-form'
import { AccountAddress } from '../AccountAddressBook/AccountAddressBook'
import LoadingButton from '../Button/LoadingButton'
import { AddressInput } from '../Form'
import Link from '../Link/Link'
import Modal from '../Modal/Modal'

export interface AccountAddressBookEditModalProps {
  /**
   * Recipient's contact info
   */
  contact: string | null
  /**
   * Recipient's current address
   */
  currentAddress: AccountAddress
  /**
   * Is deleting address
   */
  isDeletingAddress?: boolean
  /**
   * Is updating address
   */
  isUpdatingAddress?: boolean
  /**
   * Callback on delete
   */
  onDelete: () => void
  /**
   * Callback on save
   */
  onSave: (address: AccountAddress) => void
}

export default function AccountAddressBookEditModal({
  contact,
  currentAddress,
  isDeletingAddress = false,
  isUpdatingAddress = false,
  onDelete,
  onSave,
}: AccountAddressBookEditModalProps) {
  const [isEditable, setEditable] = useState<boolean>(false)
  const { handleSubmit, control } = useForm()

  const address = useMemo(
    () => ({
      address_1: currentAddress.address1 || '',
      address_2: currentAddress.address2 || '',
      attention_name: currentAddress.attentionName || '',
      city: currentAddress.city || '',
      company_name: currentAddress.companyName || '',
      country_code: currentAddress.countryCode || '',
      state: currentAddress.state || '',
      zip: currentAddress.zip || '',
    }),
    [currentAddress],
  )

  const submitForm = useCallback(
    (data: FieldValues) =>
      onSave({
        address1: data.address.address_1 || null,
        address2: data.address.address_2 || null,
        attentionName: data.address.attention_name || null,
        city: data.address.city || null,
        companyName: data.address.company_name || null,
        countryCode: data.address.country_code || null,
        facility: null,
        firstName: null,
        id: currentAddress.id,
        lastName: null,
        phoneNumber: data.address.phone_number || null,
        state: data.address.state || null,
        zip: data.address.zip || null,
      }),
    [currentAddress, onSave],
  )

  return (
    <Modal
      id="account-address-book-edit-modal"
      className="[&]:p-12"
      dialogPanelClass="[&]:max-w-[21.44rem] [&]:md:max-w-[35.31rem] w-full [&]:overflow-visible"
      afterHide={() => setEditable(false)}
      content={
        <>
          <div className="flex justify-between items-center">
            {contact && <h2 className="type-body-1">{contact}</h2>}
            {!isEditable && (
              <Link
                className="[&&]:text-neutral-700"
                onClick={(e) => {
                  e.preventDefault()
                  setEditable(true)
                }}
              >
                Edit
              </Link>
            )}
          </div>
          <form onSubmit={handleSubmit(submitForm)}>
            <Fieldset className="group" disabled={!isEditable}>
              <AddressInput
                address={address}
                className={clsx(
                  'w-full [&_.comboboxoptions]:sm-max:max-h-[200px] mb-6 md:mb-4',
                )}
                control={control}
                name="address"
                showContact={false}
                type="destination"
              />
              <LoadingButton
                className="w-full"
                disabled={isDeletingAddress}
                loading={isUpdatingAddress}
                type="submit"
              >
                Save
              </LoadingButton>
            </Fieldset>
          </form>
          <div className="mt-4 md:mt-6">
            <LoadingButton
              className="w-full"
              disabled={isUpdatingAddress}
              loading={isDeletingAddress}
              onClick={onDelete}
              variant="tertiary"
            >
              Delete
            </LoadingButton>
          </div>
        </>
      }
    />
  )
}
